import { useCallback } from 'react';
import { useQuery, skipToken } from '@tanstack/react-query';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import type { Area } from '../../types/domain';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';

type UseAreasData = {
  isLoading: boolean;
  areaTowns: Map<string, Area>;
  areaCities: Map<string, Area>;
  areaPrefectures: Map<string, Area>;
  findArea: (keyCode: string) => Area | null;
};

const EMPTY_DATA = new Map<string, never>();

const useAreasData = (): UseAreasData => {
  const { currentSimDataId } = useCurrentSimDataId();

  const { data: [areaTowns, areaCities, areaPrefectures] = [], isFetching } =
    useQuery({
      queryKey: ['areas', currentSimDataId],
      queryFn: currentSimDataId
        ? async (): Promise<
            [Map<string, Area>, Map<string, Area>, Map<string, Area>]
          > => {
            return Promise.all([
              dataApiClient
                .getAreas(currentSimDataId)
                .then((areas: Area[]): Map<string, Area> => {
                  return new Map(
                    areas.map((area: Area): [string, Area] => {
                      return [area.key_code, area];
                    })
                  );
                }),
              dataApiClient
                .getAreaCity(currentSimDataId)
                .then((areas: Area[]): Map<string, Area> => {
                  return new Map(
                    areas.map((area: Area): [string, Area] => {
                      return [area.key_code, area];
                    })
                  );
                }),
              dataApiClient
                .getAreaPref(currentSimDataId)
                .then((areas: Area[]): Map<string, Area> => {
                  return new Map(
                    areas.map((area: Area): [string, Area] => {
                      return [area.key_code, area];
                    })
                  );
                }),
            ]);
          }
        : skipToken,
      meta: { dataType: 'essentials' },
    });

  const findArea = useCallback(
    (keyCode: string): Area | null => {
      return (
        areaTowns?.get(keyCode) ??
        areaCities?.get(keyCode) ??
        areaPrefectures?.get(keyCode) ??
        null
      );
    },
    [areaTowns, areaCities, areaPrefectures]
  );

  return {
    isLoading: isFetching,
    areaTowns: areaTowns ?? EMPTY_DATA,
    areaCities: areaCities ?? EMPTY_DATA,
    areaPrefectures: areaPrefectures ?? EMPTY_DATA,
    findArea: findArea,
  };
};

export { useAreasData };
